// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { useApiError } from "./useApiError";

/**
 * Subscribes to React Query's QueryCache and MutationCache and surfaces
 * errors as Ant Design toasts via `useApiError`.
 *
 * Must be rendered inside both `<QueryClientProvider>` and `<AntdApp>`
 * so it can access the query client and the Ant Design message API.
 *
 * Individual queries/mutations can opt out by setting
 * `meta: { suppressGlobalError: true }` in their options.
 */
export default function QueryErrorHandler() {
  const queryClient = useQueryClient();
  const { handleError } = useApiError();

  useEffect(() => {
    const queryCache = queryClient.getQueryCache();
    const mutationCache = queryClient.getMutationCache();

    const unsubQuery = queryCache.subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") {
        return;
      }
      const query = event.query;
      if (query.meta?.suppressGlobalError) {
        return;
      }
      const error = event.action.error;
      handleError(error);
    });

    const unsubMutation = mutationCache.subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") {
        return;
      }
      const mutation = event.mutation;
      if (mutation.meta?.suppressGlobalError) {
        return;
      }
      const error = event.action.error;
      handleError(error);
    });

    return () => {
      unsubQuery();
      unsubMutation();
    };
  }, [queryClient, handleError]);

  return null;
}
